import { Link } from "gatsby"
import React, { FC, ReactElement } from "react"
import InlineCode from "../../components/inlineCode"
import Layout from "../../components/layout"
import { CodeExample } from "../../components/validatorFuncs"

const whenTrueExample = `var validator = new ValidatorBuilder<string>()
    .When(
        ctx => context.Value == "trap",
        ctx => 
        {
            if (context.Value == "trap")
            {
                context.AddBrokenRule("ItsATrap", "Trapped", "You fell into my trap");
            }
        })
    .Build();
    
// this will trigger the validation function to be executed
var result = validator.Validate("trap");`

const whenFalseExample = `var validator = new ValidatorBuilder<string>()
    .When(
        ctx => context.Value == "trap",
        ctx => 
        {
            if (context.Value == "trap")
            {
                context.AddBrokenRule("ItsATrap", "Trapped", "You fell into my trap");
            }
        })
    .Build();
    
// this will not trigger the validation function to be executed
var result = validator.Validate("sucker");`

const whenNotTrueExample = `var validator = new ValidatorBuilder<string>()
    .WhenNot(
        ctx => context.Value == "trap",
        ctx => 
        {
            if (context.Value == "trap")
            {
                context.AddBrokenRule("ItsATrap", "Trapped", "You fell into my trap");
            }
        })
    .Build();
    
// this will not trigger the validation function to be executed
var result = validator.Validate("trap");`

const whenNotFalseExample = `var validator = new ValidatorBuilder<string>()
    .WhenNot(
        ctx => context.Value == "trap",
        ctx => 
        {
            if (context.Value == "trap")
            {
                context.AddBrokenRule("ItsATrap", "Trapped", "You fell into my trap");
            }
        })
    .Build();
    
// this will trigger the validation function to be executed
var result = validator.Validate("sucker");`

const ifExample = `var validator = new ValidatorBuilder<string>()
    .If(ctx => context.Value == "howdy",
        (builder) => 
        {
            builder
                .NotNull()
                .Contains("how"); // no need to call Build()
        })
    .Build();

// the conditional validator will be built and executed
var result = validator.Validate("howdy");`

const continueExample = `var validator = new ValidatorBuilder<Employee>()
    .For(e => e.Age, v => v.GreaterThan(18))
    // context.IsValid is true
    .Continue((builder) => 
        {
            builder.For(e => e.DriversLicenceNo, v => v.NotNull());
        })
    // this will make context.IsValid be false
    .For(e => e.DriversLicenceNo, v => v.Null())
    // context.IsValid is now false, continue function won't execute
    .Continue((builder) => 
        {
            builder.NotNull();
        })
    .Build();

var result = validator.Validate(new Employee { Age = 22, DriversLicenceNo = "A2345" });`

function ConditionalFunctions(): ReactElement {
  return (
    <Layout>
      <h1>Conditional Functions</h1>
      <p>
        Validatum provides built-in extension methods that allow for conditional
        execution of a validation function.
      </p>
      <p>
        <ul className="list">
          {["When", "WhenNot", "If", "Continue"].map((func) => (
            <li>
              <Link to={`/functions/conditional/#${func}`}>{func}</Link>
            </li>
          ))}
        </ul>
      </p>
      <hr className="my-6" />
      <ConditionalMethod
        name="When"
        description="Executes a function when the predicate resolves to true."
        method="When<T>(Func<ValidationContext<T>, bool> predicate, ValidatorDelegate<T> func)"
      >
        <CodeExample
          label="Validation function is executed when predicate is true"
          code={whenTrueExample}
        />
        <CodeExample
          label="Validation function is not executed when predicate is false"
          code={whenFalseExample}
        />
      </ConditionalMethod>
      <hr className="my-8" />
      <ConditionalMethod
        name="WhenNot"
        description="Executes a function when the predicate resolves to false."
        method="WhenNot<T>(Func<ValidationContext<T>, bool> predicate, ValidatorDelegate<T> func)"
      >
        <CodeExample
          label="Validation function is not executed when predicate is true"
          code={whenNotTrueExample}
        />
        <CodeExample
          label="Validation function is executed when predicate is false"
          code={whenNotFalseExample}
        />
      </ConditionalMethod>
      <hr className="my-8" />
      <ConditionalMethod
        name="If"
        description={
          <>
            Allows for the inclusion of a new{" "}
            <InlineCode code="ValidatorBuilder<T>" /> when the predicate
            resolves to true.
          </>
        }
        method="If<T>(Func<ValidationContext<T>, bool> predicate, Action<IValidatorBuilder<T>> func)"
      >
        <CodeExample
          label="Validator is built and function is executed when predicate is true"
          code={ifExample}
        />
      </ConditionalMethod>
      <hr className="my-8" />
      <ConditionalMethod
        name="Continue"
        description={
          <>
            Allows for the inclusion of a new{" "}
            <InlineCode code="IValidatorBuilder<T>" /> when
            <InlineCode code="ValidationContext<T>.IsValid" /> resolves to true.
          </>
        }
        method="Continue<T>(Action<IValidatorBuilder<T>> func)"
      >
        <CodeExample code={continueExample} />
      </ConditionalMethod>
    </Layout>
  )
}

export default ConditionalFunctions

type ConditionalMethodProps = {
  name: string
  description: string | JSX.Element
  method: string
}

const ConditionalMethod: FC<ConditionalMethodProps> = ({
  name,
  description,
  method,
  children,
}) => (
  <div className="mb-4" id={name}>
    <h2 className="font-semibold">{name}</h2>
    <p className="my-4">{description}</p>
    <h5 className="font-semibold text-gray-800 text-sm">Method</h5>
    <div className="my-4">
      <InlineCode className="text-sm text-blue-600 bg-blue-100 font-semibold">
        {method}
      </InlineCode>
    </div>
    <h5 className="font-semibold text-gray-800 text-sm">Example Usage</h5>
    {children}
  </div>
)
